import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getOrderByReviewSlug } from '../lib/api';
import ReviewModal from '../components/ReviewModal';
import './ReviewPage.css';

function StarDisplay({ rating, size = 16 }) {
  return (
    <span className="rvp-stars-display">
      {[1, 2, 3, 4, 5].map(n => (
        <svg key={n} width={size} height={size} viewBox="0 0 24 24"
          fill={n <= rating ? '#F59E0B' : 'none'}
          stroke={n <= rating ? '#F59E0B' : '#D1D5DB'}
          strokeWidth="1.5">
          <path d="M12 2.5l3.09 6.26 6.91 1-5 4.87 1.18 6.88L12 17.77l-6.18 3.74L7 14.63l-5-4.87 6.91-1L12 2.5z" />
        </svg>
      ))}
    </span>
  );
}

export default function ReviewPage({ user }) {
  const { slug } = useParams();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [items, setItems]       = useState([]);
  const [modalItem, setModalItem] = useState(null);
  const [orderId, setOrderId]   = useState('');
  const [reviewSlug, setReviewSlug] = useState('');

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    getOrderByReviewSlug(slug)
      .then(res => {
        setData(res);
        setItems(res.items || []);
        setOrderId(res._id || '');
        setReviewSlug(slug || '');
        setLoading(false);
      })
      .catch(err => {
        setError(err.message || 'Unable to load review page');
        setLoading(false);
      });
  }, [slug]);

  const handleReviewed = (submittedReview) => {
    setItems(prev => prev.map(item =>
      item.orderItemIndex === modalItem.orderItemIndex
        ? { ...item, reviewed: true, existingReview: { rating: submittedReview.rating, title: submittedReview.title } }
        : item
    ));
    setModalItem(null);
  };

  const allReviewed = items.length > 0 && items.every(i => i.reviewed);

  /* ── Loading / Error ── */
  if (loading) {
    return (
      <div className="rvp-shell">
        <div className="rvp-loading">
          <span className="rvp-spinner" />
          <p>Loading your order…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rvp-shell">
        <div className="rvp-empty">
          <span className="rvp-empty__icon">🌿</span>
          <h2>Link not found</h2>
          <p>{error}</p>
          <Link to="/" className="rvp-btn-primary">Back to Shop</Link>
        </div>
      </div>
    );
  }

  /* ── Not logged in ── */
  // When the customer arrives via unique slug link, no login is needed —
  // the slug itself (sent only to their email) proves identity.
  // Only show sign-in if there is neither a user session nor a valid token.
  if (!user && !reviewSlug) {
    return (
      <div className="rvp-shell">
        <div className="rvp-empty">
          <span className="rvp-empty__icon">🌱</span>
          <h2>Sign in to leave a review</h2>
          <p>
            Please sign in to your Naturiq account to share your experience with order{' '}
            <strong>{data?.orderId}</strong>.
          </p>
          <Link
            to={`/account?redirect=/review/${slug}`}
            className="rvp-btn-primary"
          >
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="rvp-shell">
      <div className="rvp-container">

        {/* Header */}
        <div className="rvp-header">
          <span className="rvp-header__leaf">🌿</span>
          <h1 className="rvp-header__title">How did your plants arrive?</h1>
          <p className="rvp-header__sub">
            Your review helps other plant lovers make confident choices.
            Order <strong>{data?.orderId}</strong>.
          </p>
        </div>

        {/* All done state */}
        {allReviewed && (
          <div className="rvp-done">
            <span className="rvp-done__icon">🎉</span>
            <h2>Thank you for your reviews!</h2>
            <p>Your feedback helps us grow. We really appreciate it.</p>
            <Link to="/collections/plants" className="rvp-btn-primary">Shop More Plants</Link>
          </div>
        )}

        {/* Items list */}
        {!allReviewed && (
          <div className="rvp-items">
            {items.map(item => (
              <div key={item.orderItemIndex} className={`rvp-item ${item.reviewed ? 'rvp-item--reviewed' : ''}`}>
                <div className="rvp-item__img-wrap">
                  {item.image
                    ? <img src={item.image} alt={item.name} className="rvp-item__img" />
                    : <div className="rvp-item__img-placeholder">🌿</div>
                  }
                </div>
                <div className="rvp-item__info">
                  <p className="rvp-item__name">{item.name}</p>
                  {(item.selectedSize || item.selectedPlanter || item.selectedColor) && (
                    <p className="rvp-item__variant">
                      {[item.selectedSize, item.selectedPlanter, item.selectedColor].filter(Boolean).join(' · ')}
                    </p>
                  )}
                  <p className="rvp-item__qty">Qty: {item.qty}</p>

                  {item.reviewed ? (
                    <div className="rvp-item__reviewed">
                      <span className="rvp-item__reviewed-badge">✓ Reviewed</span>
                      {item.existingReview?.rating && (
                        <StarDisplay rating={item.existingReview.rating} />
                      )}
                      {item.existingReview?.title && (
                        <p className="rvp-item__reviewed-title">"{item.existingReview.title}"</p>
                      )}
                    </div>
                  ) : (
                    <button
                      className="rvp-btn-review"
                      onClick={() => setModalItem(item)}
                    >
                      Write a Review
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Already-reviewed items summary when some are pending */}
        {!allReviewed && items.some(i => i.reviewed) && (
          <p className="rvp-hint">
            {items.filter(i => i.reviewed).length} of {items.length} item{items.length > 1 ? 's' : ''} reviewed
          </p>
        )}

      </div>

      <ReviewModal
        open={!!modalItem}
        item={modalItem}
        orderId={orderId}
        onClose={() => setModalItem(null)}
        onSubmitted={handleReviewed}
        reviewSlug={reviewSlug}
      />
    </div>
  );
}